import {ScheduleType} from "../types/Type";

export const formatNumber = (
  value: number,
  locale: string = 'ko-KR',
  options: Intl.NumberFormatOptions = { style: 'decimal' }
): string => {
  return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * 주어진 숫자 값을 특정 통화 형식으로 포맷팅하는 함수.
 *
 * @param {number} value - 포맷팅할 숫자 값
 * @param {string} currency - 사용할 통화 단위 (예: 'USD', 'EUR', 'KRW')
 * @param {string} [locale='ko-KR'] - 사용할 로케일 (기본값은 'ko-KR')
 *
 * @returns {string} 포맷팅된 통화 문자열 ('원' 단위 포함)
 */
export const formatCurrency = (
  value: number,
  currency: string,
  locale: string = 'ko-KR'
): string => {
  const numericPart = formatNumber(Math.abs(value), locale, { style: 'currency', currency })
    .replace(/[^0-9,]/g, '');

  const sign = value < 0 ? '-' : '';

  return `${sign}${numericPart}원`;
};

/**
 * 주어진 전화번호 문자열을 형식화하여 반환합니다.
 * 10자리 전화번호는 `02-1234-5678` 형식으로,
 * 11자리 전화번호는 `010-1234-5678` 형식으로 변환됩니다.
 *
 * @param {string} phoneNumber - 형식화할 전화번호 문자열
 * @returns {string} 형식화된 전화번호 문자열
 * @throws {Error} 유효하지 않은 전화번호 형식일 경우 오류를 발생시킵니다.
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // 문자열이 10자리 또는 11자리인지 확인
  if (!/^\d{10,11}$/.test(phoneNumber)) {
    console.log('유효하지 않은 전화번호 형식입니다.');
    return phoneNumber;
  }

  if (phoneNumber.startsWith('010')) {
    if (phoneNumber.length === 10) {
      return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    if (phoneNumber.length === 11) {
      return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  }

  if (phoneNumber.startsWith('10')) {
    if (phoneNumber.length === 9) {
      return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    if (phoneNumber.length === 10) {
      return phoneNumber.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  }

  if (phoneNumber.startsWith('02')) {
    if (phoneNumber.length === 9) {
      return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    if (phoneNumber.length === 10) {
      return phoneNumber.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  }

  const localDialNumbers = ['051', '053', '032', '062', '042', '052', '044', '031', '033', '043', '041', '063', '061', '054', '055', '064'];
  const isPhoneNumberStartsWithLocalDialNumber = localDialNumbers.some(localDialNumber => phoneNumber.startsWith(localDialNumber));
  if (isPhoneNumberStartsWithLocalDialNumber) {
    if (phoneNumber.length === 10) {
      return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    if (phoneNumber.length === 11) {
      return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  }

  return phoneNumber;
};


export const parseCronExpression = (cronExpression: string): string => {
  const parts = cronExpression.trim().split(' ');

  if (parts.length < 5 || parts.length > 6) return '잘못된 크론 표현식입니다.';

  const [second = '*', minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // 각 필드에 대해 특정 범위에서 자연어로 변환하는 함수
  const parseField = (field: string, name: string): string => {
    if (field === '*') return `${name}마다`;
    if (field.includes(',')) return `${name} 중 ${field}에 실행`;
    if (field.includes('-')) {
      const [start, end] = field.split('-');
      return `${name} ${start}에서 ${end}까지 실행`;
    }
    if (field.includes('/')) {
      const [start, step] = field.split('/');
      return `${name} ${start}부터 매 ${step} 간격으로 실행`;
    }
    return `${name} ${field}에 실행`;
  };

  const secondsText = parseField(second, '초');
  const minutesText = parseField(minute, '분');
  const hoursText = parseField(hour, '시');
  const dayOfMonthText = parseField(dayOfMonth, '일');
  const monthsText = parseField(month, '월');
  const daysOfWeekText = parseField(dayOfWeek, '요일');

  return `${secondsText}, ${minutesText}, ${hoursText}, ${dayOfMonthText}, ${monthsText}, ${daysOfWeekText}에 실행됩니다.`;
}

export const getBaechaPriceTypeStr = (str: string): string => {
  if (str === 'new') return '신단가';
  if (str === 'mid') return '중단가';
  if (str === 'old') return '구단가';
  return '';
};

export const getShortBaechaPriceTypeStr = (str: string): string => {
  if (str === 'new') return '신';
  if (str === 'mid') return '중';
  if (str === 'old') return '구';
  return '';
};

export const getBaechaPriceReceiveMethodStr = (str: string) => {
  if (str === 'direct_ad') return '직수';
  if (str === 'direct_noad') return '현수';
  if (str === 'sign') return '싸인';
  return '';
};

export const getBaechaMHTypeStr = (mhType: string) => {
  if (mhType === '1h') return '1시간';
  if (mhType === '2h') return '2시간';
  if (mhType === '4h') return '반나절';
  if (mhType === '1d') return '하루';
  return '';
}

export const getBaechaStatusStr = (str: string) => {
  if (str === 'balju') return '발주';
  if (str === 'suju') return '수주';
  if (str === 'designated_reject') return '지명 거절';
  if (str === 'suju_zero') return '수주 없음';
  if (str === 'balju_cancel') return '발주 취소';
  if (str === 'complete') return '완료';
	if (str === 'delete') return '삭제';
	if (str === 'save') return '임시';
  return '';
};

export const getStatusColor = (str: string): string => {
	if (str === 'balju') return 'grey';
	if (str === 'suju') return 'blue';
	if (str === 'designated_reject') return 'red';
	if (str === 'suju_zero') return 'orange';
	if (str === 'balju_cancel') return 'red';
	if (str === 'complete') return 'green';
	if (str === 'delete') return 'red';
	if (str === 'save') return 'purple';
	return '';
};

export const getRegionPrefix = (str?: string) => {
  if (!str || str.length === 0) return '';

  let region = str.split(' ')[0];
  region = region.replaceAll('충청북도', '충북');
  region = region.replaceAll('충청남도', '충남');
  region = region.replaceAll('전라남도', '전남');
  region = region.replaceAll('전라북도', '전북');
  region = region.replaceAll('경상남도', '경남');
  region = region.replaceAll('경상북도', '경북');
  return region
    .replaceAll('특별시', '')
    .replaceAll('광역시', '')
    .replaceAll('특별자치시', '')
    .replaceAll('특별자치도', '')
    .replaceAll('도', '')
    .trim()
};

export const getWorkPeriodStr = ({ workDay, workHour }: { workDay?: any, workHour?: any}) => {
  let result = '';

  if (workDay) {
    result = workDay === 1 ? '하루' : `${workDay}일`;
  } else if (workHour) {
    if (workHour === 1) result = '1시간';
    else if (workHour === 2) result = '2시간';
    else if (workHour === 4) result = '반나절';
  }
  return result;
}

export const getScheduleStr = (schedule?: ScheduleType) => {
  if (schedule === 'am') return '오전';
  if (schedule === 'pm') return '오후';
  if (schedule === 'ready') return '대기';
  if (schedule === 'day') return '하루';
  if (schedule === 'dayoff') return '휴무';
  if (schedule === 'absence') return '불참';
  return '';
};

export const removeNil = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v != null)
  );

export const getSubscriptionStatusStr = (status: string) => {
  switch (status) {
    case 'ACTIVE':
    case 'CANCELED':
    case 'IN_GRACE':
      return '구독중';
    case 'EXPIRED':
      return '구독 만료'
    case 'REVOKED':
    case 'REFUNDED':
      return '환불';
    default:
      return status;
  }
};
